import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { APP_CONFIG } from '../utils/config';
import logo from '../assets/logo.png';

export default function AppFooter({ type = 'retailer' }) {
  const year = new Date().getFullYear();

  // Navigation Links tailored to portal context
  const getNavContent = () => {
    if (type === 'admin') {
      return {
        sectionTitle: 'Admin Operations',
        links: [
          { to: '/admin', label: 'Dashboard' },
          { to: '/admin/daily-ledger', label: 'Daily Sheet' },
          { to: '/admin/retailers', label: 'Retailers' },
          { to: '/admin/company-order', label: 'Company Order' },
          { to: '/admin/inventory', label: 'Inventory' },
          { to: '/admin/ledger', label: 'Ledger' },
          { to: '/admin/settings', label: 'Settings' },
        ],
        supportTitle: 'Management & Tech',
        supportLinks: [
          { to: '/admin/support', label: 'Support Tickets' },
          { to: '/admin/announcements', label: 'Announcements' },
        ],
      };
    }

    if (type === 'landing') {
      return {
        sectionTitle: 'Lucy Garden',
        links: [
          { to: '/about', label: 'About Us' },
          { to: '/prices', label: 'Products & Price List' },
          { to: '/terms', label: 'Terms of Supply' },
          { to: '/privacy', label: 'Privacy Policy' },
          { to: '/login', label: 'Retailer Portal Login' },
        ],
        supportTitle: 'Retail Partnership',
        supportLinks: [
          { to: '/prices', label: 'Wholesale Rates' },
          { to: '/support', label: 'Inquiries & Support' },
        ],
      };
    }

    // Default: Retailer Portal
    return {
      sectionTitle: 'Retailer Services',
      links: [
        { to: '/', label: 'Home' },
        { to: '/order', label: 'Place Order' },
        { to: '/track', label: 'My Orders' },
        { to: '/history', label: 'Order History' },
        { to: '/prices', label: 'Price List' },
        { to: '/my-ledger', label: 'My Ledger' },
        { to: '/profile', label: 'My Account' },
      ],
      supportTitle: 'Help & Contact',
      supportLinks: [
        { to: '/support', label: 'Support Tickets' },
        { to: '/about', label: 'About Lucy Garden' },
      ],
    };
  };

  const nav = getNavContent();

  return (
    <footer className="mt-auto bg-[#faf9f6] dark:bg-[#121110] border-t border-[#e5e2db] dark:border-[#262422]">
      {/* Desktop Footer: Balanced, Professional, Integrated */}
      <div className="hidden lg:block">
        <div className="max-w-6xl mx-auto px-6 pt-9 pb-6">
          <div className="grid grid-cols-12 gap-8 pb-7 border-b border-[#e8e5de] dark:border-[#22211f]">

            {/* Col 1: Brand & Proprietor (4 cols) */}
            <div className="col-span-4 pr-4">
              <div className="flex items-center gap-3">
                <img
                  src={logo}
                  alt="Lucy Garden"
                  className="w-10 h-10 rounded-lg object-contain bg-white dark:bg-[#1a1917] p-0.5 border border-[#e2dfd7] dark:border-[#2c2a27] shadow-2xs"
                />
                <div>
                  <h3 className="text-sm font-bold text-[#1a1917] dark:text-[#f5f4f0] leading-none">
                    {APP_CONFIG.appName}
                  </h3>
                  <p className="text-[10px] text-[#736d65] dark:text-[#9e9a92] mt-0.5">
                    {APP_CONFIG.tagline}
                  </p>
                </div>
              </div>

              <p className="text-xs text-[#736d65] dark:text-[#9e9a92] mt-3 leading-relaxed">
                Proprietor: <span className="font-semibold text-[#1a1917] dark:text-[#f5f4f0]">Lal Babu Gupta</span>
              </p>

              <p className="text-xs text-[#736d65] dark:text-[#9e9a92] mt-1.5 leading-relaxed">
                Fresh milk & dairy distribution serving 70+ retail partners across Madhubani, Bihar.
              </p>
            </div>

            {/* Col 2: Contextual Portal Links (2.5 cols) */}
            <div className="col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#8c857b] dark:text-[#7d7870] mb-2.5">
                {nav.sectionTitle}
              </p>
              <div className="space-y-1.5">
                {nav.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-xs text-[#524d47] dark:text-[#b5b1a8] hover:text-[#1e3a5f] dark:hover:text-blue-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Col 3: Support & Contact (2.5 cols) */}
            <div className="col-span-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#8c857b] dark:text-[#7d7870] mb-2.5">
                {nav.supportTitle}
              </p>
              <div className="space-y-2">
                <a
                  href={`tel:+91${APP_CONFIG.phone}`}
                  className="flex items-center gap-2 text-xs text-[#524d47] dark:text-[#b5b1a8] hover:text-[#1e3a5f] dark:hover:text-blue-400 transition-colors"
                >
                  <Phone size={13} className="text-[#8c857b] shrink-0" />
                  <span>+91 {APP_CONFIG.phone}</span>
                </a>

                <a
                  href={`https://wa.me/91${APP_CONFIG.phone}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 transition-colors"
                >
                  <FaWhatsapp size={13} className="text-[#25D366] shrink-0" />
                  <span>WhatsApp Chat</span>
                </a>

                <div className="flex items-start gap-2 text-xs text-[#736d65] dark:text-[#9e9a92]">
                  <MapPin size={13} className="text-[#8c857b] shrink-0 mt-0.5" />
                  <span>Madhubani, Bihar</span>
                </div>

                <div className="pt-1 space-y-1">
                  {nav.supportLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="block text-[11px] text-[#1e3a5f] dark:text-blue-400 hover:underline"
                    >
                      {link.label} &rarr;
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Col 4: Makeward Labs Agency Attribution (3 cols) */}
            <div className="col-span-3 pl-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#8c857b] dark:text-[#7d7870] mb-2.5">
                Engineering Partner
              </p>
              <div className="space-y-1.5">
                <a
                  href={APP_CONFIG.developer.portfolio}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 group"
                >
                  <img
                    src="/MAKEWARD_logo.png"
                    alt="Makeward Labs"
                    className="w-5 h-5 object-contain shrink-0"
                  />
                  <span className="text-xs font-bold text-[#1a1917] dark:text-[#f5f4f0] group-hover:text-[#1e3a5f] dark:group-hover:text-blue-400 transition-colors">
                    {APP_CONFIG.developer.name}
                  </span>
                  <ArrowUpRight size={12} className="text-[#8c857b] group-hover:text-[#1e3a5f] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </a>

                <p className="text-[11px] text-[#736d65] dark:text-[#9e9a92] leading-normal">
                  {APP_CONFIG.developer.tagline}
                </p>

                <p className="text-[10px] text-[#8c857b] dark:text-[#7d7870] leading-relaxed">
                  Enterprise cloud apps & digital platforms.
                </p>
              </div>
            </div>

          </div>

          {/* Desktop Bottom Bar */}
          <div className="pt-4 flex items-center justify-between text-xs text-[#8c857b] dark:text-[#7d7870]">
            <div className="flex items-center gap-3">
              <p>&copy; {year} {APP_CONFIG.appName}. All rights reserved.</p>
              <span>&bull;</span>
              <Link to="/privacy" className="hover:text-[#1a1917] dark:hover:text-[#f5f4f0] transition-colors">Privacy</Link>
              <span>&bull;</span>
              <Link to="/terms" className="hover:text-[#1a1917] dark:hover:text-[#f5f4f0] transition-colors">Terms</Link>
            </div>

            <div className="flex items-center gap-1.5 text-[11px]">
              <span>Engineered by</span>
              <a
                href={APP_CONFIG.developer.portfolio}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 font-bold text-[#1a1917] dark:text-[#f5f4f0] hover:text-[#1e3a5f] dark:hover:text-blue-400 transition-colors"
              >
                <span className="w-4 h-4 rounded bg-white flex items-center justify-center p-[1.5px] shadow-xs shrink-0 border border-black/5">
                  <img src="/MAKEWARD_logo.png" alt="Makeward Labs" className="w-full h-full object-contain" />
                </span>
                <span>{APP_CONFIG.developer.name}</span>
              </a>
              <span className="font-mono text-[10px] text-[#a8a29a]">&bull; v{APP_CONFIG.version}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Footer: Clean, Minimal, Non-Intrusive */}
      <div className={`lg:hidden px-4 pt-4 text-center text-xs text-[#8c857b] dark:text-[#7d7870] border-t border-[#e8e5de]/60 dark:border-[#22211f]/60 ${
        type === 'landing' ? 'pb-6' : 'pb-20'
      }`}>
        <div className="flex items-center justify-center gap-1.5 text-[11px]">
          <span>Engineered by</span>
          <a
            href={APP_CONFIG.developer.portfolio}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 font-bold text-[#1a1917] dark:text-[#f5f4f0] hover:text-[#1e3a5f] transition-colors"
          >
            <span className="w-4 h-4 rounded bg-white flex items-center justify-center p-[1.5px] shadow-xs shrink-0 border border-black/5">
              <img src="/MAKEWARD_logo.png" alt="Makeward Labs" className="w-full h-full object-contain" />
            </span>
            <span>{APP_CONFIG.developer.name}</span>
            <ArrowUpRight size={11} className="text-[#8c857b]" />
          </a>
        </div>

        <p className="text-[10px] text-[#a8a29a] dark:text-[#555] mt-1.5">
          &copy; {year} {APP_CONFIG.appName} &bull; v{APP_CONFIG.version}
        </p>
      </div>
    </footer>
  );
}
